import { useMemo, useState } from "react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { ProviderCard, type ProviderCardData } from "@/components/services/ProviderCard";
import { FeaturedSpotlight } from "@/components/services/FeaturedSpotlight";
import { useTranslation } from "react-i18next";
import {
  useEngineCoaches,
  coachRowToCard,
  useProviderProfileCoaches,
  providerProfileRowToCard,
} from "@/lib/engineSurfaces";
import PillarFAQ, { type FAQItem } from "@/components/shared/PillarFAQ";
import { DiscoveryCallModal } from "@/components/shared/DiscoveryCallModal";
import { Button } from "@/components/ui/button";
import BecomeProviderCTA from "@/components/shared/BecomeProviderCTA";
import { VelyraHeroSplit } from "@/components/services/VelyraHeroSplit";

const LANGS = ["en", "fr", "es", "pt"];

export default function Coaching() {
  const { t, i18n } = useTranslation();
  const lang: "en" | "fr" = (i18n.language || "en").startsWith("fr") ? "fr" : "en";

  const [exp, setExp] = useState<string | null>(null);
  const [langF, setLangF] = useState<string | null>(null);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  const { data: engineRows = [] } = useEngineCoaches();
  const { data: ppRows = [] } = useProviderProfileCoaches("coach");

  const cards: ProviderCardData[] = useMemo(
    () => {
      const engineCards = engineRows.map(coachRowToCard);
      const ppCards = ppRows.map((e) => providerProfileRowToCard(e, lang));
      const seen = new Set<string>();
      return [...engineCards, ...ppCards].filter((c) => {
        if (seen.has(c.user_id)) return false;
        seen.add(c.user_id);
        return true;
      });
    },
    [engineRows, ppRows, lang],
  );

  const allExpertise = useMemo(
    () => Array.from(new Set(cards.flatMap((c) => c.expertise_areas ?? []))).sort(),
    [cards],
  );

  const filtered = useMemo(() => {
    return cards.filter((p) => {
      if (exp && !(p.expertise_areas ?? []).includes(exp)) return false;
      if (langF && !(p.languages ?? []).includes(langF)) return false;
      return true;
    });
  }, [cards, exp, langF]);

  const noFilters = !exp && !langF;
  const featured = useMemo(
    () => (noFilters ? filtered.filter((p) => p.featured === true).slice(0, 3) : []),
    [filtered, noFilters],
  );
  const featuredIds = useMemo(() => new Set(featured.map((p) => p.user_id)), [featured]);
  const grid = useMemo(
    () => filtered.filter((p) => !featuredIds.has(p.user_id)),
    [filtered, featuredIds],
  );

  // ── Section copy (verbatim from spec) ──────────────────────────────────────
  const HOW = {
    eyebrow: { en: "— HOW IT WORKS", fr: "— COMMENT ÇA MARCHE" },
    headline: { en: "Four steps to a coach who moves things", fr: "Quatre étapes vers un coach qui fait bouger les choses" },
    steps: [
      {
        n: "01",
        title: { en: "Discovery", fr: "Découverte" },
        body: {
          en: "Book a free 20-minute call. We map your stage, your blockers, and what 'good' looks like 90 days from now.",
          fr: "Réservez un appel gratuit de 20 minutes. Nous cartographions votre stade, vos blocages, et ce à quoi ressemble la réussite à 90 jours.",
        },
      },
      {
        n: "02",
        title: { en: "Matching", fr: "Mise en relation" },
        body: {
          en: "We pair you with the operator-coach whose own scar tissue maps to your problem. Not a generalist — a practitioner who's lived it.",
          fr: "Nous vous associons au coach-opérateur dont l'expérience colle à votre problème. Pas un généraliste — un praticien qui l'a vécu.",
        },
      },
      {
        n: "03",
        title: { en: "First session", fr: "Première session" },
        body: {
          en: "Within a week. You leave with a written diagnosis, a 30-day action plan, and the next decision identified — not just notes.",
          fr: "Sous une semaine. Vous repartez avec un diagnostic écrit, un plan d'action à 30 jours, et la prochaine décision identifiée — pas juste des notes.",
        },
      },
      {
        n: "04",
        title: { en: "Ongoing rhythm", fr: "Cadence régulière" },
        body: {
          en: "Single session, 4-pack, or monthly retainer. You set the cadence; review every 90 days and decide whether to keep going. You're never locked in.",
          fr: "Séance unique, pack de 4, ou mensuel. Vous fixez le rythme ; revue tous les 90 jours pour décider si on continue. Vous n'êtes jamais enfermé dans un contrat.",
        },
      },
    ],
  };

  const CONTRAST = {
    eyebrow: { en: "— THE DIFFERENCE", fr: "— LA DIFFÉRENCE" },
    headline: { en: "Operators, not consultants", fr: "Opérateurs, pas consultants" },
    intro: {
      en: "Most advice you find online comes from people who teach for a living. Our coaches built and ran businesses — they teach because they have something specific to share.",
      fr: "La plupart des conseils en ligne viennent de personnes qui enseignent pour vivre. Nos coachs ont construit et dirigé des entreprises — ils enseignent parce qu'ils ont quelque chose de spécifique à transmettre.",
    },
    rows: [
      {
        a: { en: "Speak in frameworks", fr: "Parlent en frameworks" },
        b: { en: "Speak from scar tissue", fr: "Parlent depuis l'expérience" },
      },
      {
        a: { en: "Optimize for slides", fr: "Optimisent pour les slides" },
        b: { en: "Optimize for the next decision", fr: "Optimisent pour la prochaine décision" },
      },
      {
        a: { en: "Charge for hours", fr: "Facturent à l'heure" },
        b: { en: "Are accountable to outcomes", fr: "Sont responsables des résultats" },
      },
      {
        a: { en: "Borrowed credibility (logos)", fr: "Crédibilité empruntée (logos)" },
        b: { en: "Earned credibility (P&L scars)", fr: "Crédibilité gagnée (cicatrices de P&L)" },
      },
      {
        a: { en: "Sell you on the next phase", fr: "Vous vendent la phase suivante" },
        b: { en: "Tell you when you don't need them", fr: "Vous disent quand vous n'avez plus besoin d'eux" },
      },
    ],
    closing: {
      en: "If your coach has never made the decision you're about to make, the advice is theory. We don't ship theory.",
      fr: "Si votre coach n'a jamais pris la décision que vous êtes sur le point de prendre, le conseil est théorique. Nous ne livrons pas de théorie.",
    },
  };

  const FAQ_ITEMS: FAQItem[] = [
    {
      question: {
        en: "How much does coaching cost?",
        fr: "Combien coûte le coaching ?",
      },
      answer: {
        en: "Single sessions start at €150–€300 depending on the coach's seniority. Packages of 4 sessions run €500–€1,000. Monthly retainers (2–4 hours/month) range from €400 to €1,500. All prices are visible on each coach's profile, no quote needed.",
        fr: "Les sessions individuelles commencent entre 150 € et 300 € selon la séniorité du coach. Les packs de 4 sessions vont de 500 € à 1 000 €. Les retainers mensuels (2–4 h/mois) vont de 400 € à 1 500 €. Tous les prix sont affichés sur le profil de chaque coach, sans devis.",
      },
    },
    {
      question: {
        en: "Can I pay in CFA, NGN, KES, or BRL?",
        fr: "Puis-je payer en CFA, NGN, KES ou BRL ?",
      },
      answer: {
        en: "Yes. We accept Wave, Orange Money, MTN Mobile Money (CFA zone), MPESA (Kenya), and bank transfer in BRL and NGN. Payment links are generated at checkout in your local currency.",
        fr: "Oui. Nous acceptons Wave, Orange Money, MTN Mobile Money (zone CFA), MPESA (Kenya), et virement bancaire en BRL et NGN. Les liens de paiement sont générés au checkout dans votre devise locale.",
      },
    },
    {
      question: {
        en: "What if my coach isn't a good fit?",
        fr: "Et si mon coach ne convient pas ?",
      },
      answer: {
        en: "Tell us after the first session. We'll coordinate a rematch with another coach on the platform — no additional Bootpreneurs fee, and any unused balance transfers over (or we refund it). Fit matters more than expertise. A coach you don't trust can't help you.",
        fr: "Dites-le nous après la première séance. Nous coordonnons une mise en relation avec un autre coach de la plateforme — pas de frais supplémentaires côté Bootpreneurs, et tout solde non utilisé est reporté (ou remboursé). Le fit compte plus que l'expertise. Un coach à qui vous ne faites pas confiance ne peut pas vous aider.",
      },
    },
    {
      question: {
        en: "Do you offer refunds?",
        fr: "Proposez-vous des remboursements ?",
      },
      answer: {
        en: "Yes. Unused sessions are refundable for 30 days, no questions asked. Completed sessions are non-refundable, but if you're unhappy with one, message us and we'll make it right.",
        fr: "Oui. Les sessions non utilisées sont remboursables pendant 30 jours, sans condition. Les sessions effectuées ne sont pas remboursables, mais si vous n'êtes pas satisfait, contactez-nous et nous trouverons une solution.",
      },
    },
    {
      question: {
        en: "How is this different from mentoring?",
        fr: "En quoi est-ce différent du mentoring ?",
      },
      answer: {
        en: "Coaching is structured, paid, and time-boxed: 60–90 minute sessions with a written plan and accountability. Mentoring (with Bruno) is a longer arc — multi-month engagements for founders who want a senior operator alongside them through major transitions.",
        fr: "Le coaching est structuré, payant et limité dans le temps : sessions de 60–90 minutes avec plan écrit et suivi. Le mentoring (avec Bruno) est un parcours plus long — engagements de plusieurs mois pour les dirigeants qui veulent un opérateur senior à leurs côtés à travers les transitions majeures.",
      },
    },
    {
      question: {
        en: "Are sessions in English or French?",
        fr: "Les sessions sont-elles en anglais ou en français ?",
      },
      answer: {
        en: "Both. Each coach lists the languages they work in (EN, FR, and sometimes PT or ES). Filter the directory by language to see only the coaches who match yours.",
        fr: "Les deux. Chaque coach indique les langues dans lesquelles il travaille (EN, FR, et parfois PT ou ES). Filtrez le répertoire par langue pour ne voir que les coachs qui correspondent à la vôtre.",
      },
    },
  ];

  const FINAL_CTA = {
    headline: {
      en: "Ready to meet a coach?",
      fr: "Prêt à rencontrer un coach ?",
    },
    body: {
      en: "Browse 8 operator-coaches above, or book a 30-minute discovery call and we'll match you to the right one.",
      fr: "Parcourez les 8 coachs-opérateurs ci-dessus, ou réservez un appel découverte de 30 minutes et nous vous mettons en relation avec le bon.",
    },
    primary: { en: "Browse coaches", fr: "Parcourir les coachs" },
    secondary: { en: "Book discovery call →", fr: "Réserver un appel découverte →" },
  };

  function scrollToCoaches() {
    const el = document.getElementById("coach-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <PublicShell>
      <Seo title={t("services.coaching.seoTitle")} description={t("services.coaching.subtitle")} />
      <section className="container pt-10 md:pt-14">
        <VelyraHeroSplit humansAnchor="coach-grid" />
      </section>
      <section id="coach-grid" className="container py-6 md:py-10 scroll-mt-20">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <select
            value={exp ?? ""}
            onChange={(e) => setExp(e.target.value || null)}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t("services.coaching.allExpertise")}</option>
            {allExpertise.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <select
            value={langF ?? ""}
            onChange={(e) => setLangF(e.target.value || null)}
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t("services.coaching.allLanguages")}</option>
            {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </div>
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {t("services.empty.results")}
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <div className="mb-10">
                <div className="mb-4 flex items-end justify-between gap-3">
                  <div>
                    <div className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                      {lang === "fr" ? "À l'honneur" : "Featured"}
                    </div>
                    <h2 className="mt-1 font-display text-2xl font-bold tracking-tight md:text-3xl">
                      {lang === "fr" ? "Sélection de l'équipe" : "Editor's picks"}
                    </h2>
                  </div>
                </div>
                <FeaturedSpotlight items={featured} />
              </div>
            )}
            {featured.length > 0 && grid.length > 0 && (
              <h3 className="mb-4 font-display text-xl font-bold tracking-tight">
                {lang === "fr" ? "Tous les coachs" : "All coaches"}
              </h3>
            )}
            {grid.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grid.map((p) => <ProviderCard key={p.user_id} p={p} />)}
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Section A: How coaching works ───────────────────────────────── */}
      <section className="container py-12 md:py-16">
        <div className="font-mono text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--learn-warning))" }}>
          {HOW.eyebrow[lang]}
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
          {HOW.headline[lang]}
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HOW.steps.map((s) => (
            <div key={s.n} className="rounded-2xl border border-border bg-card p-6">
              <div className="font-mono text-sm font-bold text-primary">{s.n}</div>
              <h3 className="mt-3 font-display text-lg font-bold">{s.title[lang]}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body[lang]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section C: Operators vs Consultants ─────────────────────────── */}
      <section className="container py-12 md:py-16">
        <div className="font-mono text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--learn-warning))" }}>
          {CONTRAST.eyebrow[lang]}
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
          {CONTRAST.headline[lang]}
        </h2>
        <p className="mt-4 max-w-3xl leading-relaxed text-muted-foreground">
          {CONTRAST.intro[lang]}
        </p>

        <div className="mt-8 overflow-hidden rounded-2xl border border-border">
          <div className="grid grid-cols-2 border-b border-border bg-muted/30 text-xs font-semibold uppercase tracking-wider">
            <div className="px-4 py-3 text-muted-foreground">{lang === "fr" ? "Consultants" : "Consultants"}</div>
            <div className="px-4 py-3 text-primary">{lang === "fr" ? "Opérateurs" : "Operators"}</div>
          </div>
          {CONTRAST.rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-2 ${i < CONTRAST.rows.length - 1 ? "border-b border-border" : ""}`}>
              <div className="px-4 py-4 text-sm text-foreground/70">{row.a[lang]}</div>
              <div className="px-4 py-4 text-sm font-medium text-foreground">{row.b[lang]}</div>
            </div>
          ))}
        </div>

        <p className="mt-6 max-w-3xl border-l-2 border-primary pl-4 italic leading-relaxed">
          {CONTRAST.closing[lang]}
        </p>
      </section>

      {/* ── Section D: FAQ ──────────────────────────────────────────────── */}
      <PillarFAQ lang={lang} items={FAQ_ITEMS} />

      {/* ── Section E: Final CTA ────────────────────────────────────────── */}
      <section className="container py-12 md:py-16">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-12 text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {FINAL_CTA.headline[lang]}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
            {FINAL_CTA.body[lang]}
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6">
            <Button size="lg" onClick={scrollToCoaches}>
              {FINAL_CTA.primary[lang]}
            </Button>
            <button
              type="button"
              onClick={() => setDiscoveryOpen(true)}
              className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
            >
              {FINAL_CTA.secondary[lang]}
            </button>
          </div>
        </div>
      </section>

      <DiscoveryCallModal
        open={discoveryOpen}
        onOpenChange={setDiscoveryOpen}
        sourcePillar="coaching"
        lang={lang}
      />
      <div className="container mx-auto px-4">
        <BecomeProviderCTA kind="coach" />
      </div>
    </PublicShell>
  );
}
